// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { useContext } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { groupBy } from 'lodash';
import { CheckIcon, ChevronDown, ChevronUp, ListTodoIcon } from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useAuth } from 'hooks/useAuth';
import useTMSContext from 'hooks/useTMSContext';
import { Maybe, Undef } from 'types/UtilityTypes';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import { SuggestionStatus } from 'types/suggestions/LoadSuggestions';
import { flattenSuggestionChanges } from 'utils/flattenSuggestionChanges';
import { isQuoteRequestActionable } from 'utils/isQuoteRequestActionable';
import { cn } from 'utils/shadcn';

import { KIT_ANIMATIONS, SUGGESTIONS_PAGE_SIZE } from '../constants';
import { isBlockedSpecialTransportSuggestion } from '../utils/kitSuggestionEligibility';
import {
  KitCategoryToggleAction,
  captureKitCategoryToggled,
  captureKitSuggestionPreviewOpened,
  captureKitSuggestionsLoadMore,
  getPosthogCapture,
} from '../utils/posthogEvents';
import {
  AppointmentDisplayTimezones,
  getCategoryButtonLabel,
  getQuickQuoteBatchLabel,
  getRepeatedLoadBuildingIds,
  getSuggestionTitleParts,
  shouldShowBatchLTLDestinationOnly,
} from '../utils/suggestionFormatters';
import { hasPreviewableSuggestionChanges } from '../utils/suggestionPreviewConfig';
import { Suggestion } from './Suggestion';
import { SuggestionCategoryIcon } from './SuggestionCategoryIcon';

export type SuggestionCategoryProps = {
  additionalSuggestions?: {
    rows: ReactNode;
    count: number;
    pendingCount: number;
    hasSelection: boolean;
  };
  pipelineEnum: SuggestionPipelines;
  suggestions: Undef<GenericSuggestion[]>;
  clickedSuggestion: Maybe<GenericSuggestion>;
  expandedPipeline: Maybe<SuggestionPipelines>;
  setExpandedPipeline: Dispatch<SetStateAction<Maybe<SuggestionPipelines>>>;
  visibleCountByPipeline: Partial<Record<SuggestionPipelines, number>>;
  setVisibleCountByPipeline: Dispatch<
    SetStateAction<Partial<Record<SuggestionPipelines, number>>>
  >;
  isBatchQuoteEnabled: boolean;
  // Kit <-> Batch Quote selection sync (suggestion ids currently in the batch).
  batchSelectedSuggestionIds: number[];
  // Suggestion id whose Preview tooltip is open (shared across categories).
  openPreviewSuggestionId: Maybe<number>;
  setOpenPreviewSuggestionId: Dispatch<SetStateAction<Maybe<number>>>;
  // Close an open Preview when the pointer enters a different suggestion row.
  onSuggestionRowPointerEnter: (suggestionId: number) => void;
  handleApplySuggestion: (suggestion: GenericSuggestion) => void;
  handleApplyBatch: (suggestions: GenericSuggestion[]) => void;
  handleDismissSuggestion: (suggestion: GenericSuggestion) => void;
  // Load stop timezones for Appointment pill titles (never browser-local).
  appointmentTimezones?: AppointmentDisplayTimezones;
  // Preferred when Kit supplies TMS outside / ahead of context.
  tmsName?: Maybe<string>;
};

// Suggestion category (Quick Quote, Load Building, etc.): header + list of suggestion pills
export function SuggestionCategory({
  additionalSuggestions,
  pipelineEnum,
  suggestions,
  clickedSuggestion,
  expandedPipeline,
  setExpandedPipeline,
  visibleCountByPipeline,
  setVisibleCountByPipeline,
  isBatchQuoteEnabled,
  batchSelectedSuggestionIds,
  openPreviewSuggestionId,
  setOpenPreviewSuggestionId,
  onSuggestionRowPointerEnter,
  handleApplySuggestion,
  handleApplyBatch,
  handleDismissSuggestion,
  appointmentTimezones,
  tmsName: tmsNameProp,
}: SuggestionCategoryProps) {
  const posthog = usePostHog();
  const { user } = useAuth();
  const { tmsName: tmsNameFromContext } = useTMSContext();
  const {
    currentState: { batchLTLReplySummary },
  } = useContext(SidebarStateContext);
  const titleContext = {
    tmsName: tmsNameProp || tmsNameFromContext || undefined,
    appointmentTimezones,
  };
  const posthogCapture = getPosthogCapture(posthog);

  const validSuggestions = filterValidDisplaySuggestions(suggestions ?? []);
  const totalSuggestionCount =
    validSuggestions.length + (additionalSuggestions?.count ?? 0);
  if (totalSuggestionCount === 0) {
    return null;
  }

  const pendingSuggestions = validSuggestions.filter(
    (s) => s.status !== SuggestionStatus.Accepted
  );
  const pendingCount =
    pendingSuggestions.length + (additionalSuggestions?.pendingCount ?? 0);
  const isPipelineCompleted = pendingCount === 0;

  const isQuickQuote = pipelineEnum === SuggestionPipelines.QuickQuote;
  // Active Quick Quotes are one selectable batch, even when lanes originated
  // from different source emails. Handled history keeps its source-email grouping.
  const quickQuoteBatches = isQuickQuote ? [validSuggestions] : null;
  const hasSelectedPill =
    additionalSuggestions?.hasSelection ||
    validSuggestions.some(
      (s) =>
        clickedSuggestion?.id === s.id ||
        batchSelectedSuggestionIds.includes(s.id)
    );
  const isExpanded = expandedPipeline === pipelineEnum;
  let categoryStateClass =
    'border-neutral-300 bg-neutral-50 hover:border-neutral-300 hover:bg-neutral-100';
  if (hasSelectedPill) categoryStateClass = 'border-accent-400 bg-accent-50/60';
  else if (isPipelineCompleted)
    categoryStateClass = 'border-neutral-300 bg-neutral-100';

  const handlePreviewOpenChange = (
    suggestion: GenericSuggestion,
    open: boolean
  ) => {
    if (open) {
      captureKitSuggestionPreviewOpened({
        capture: posthogCapture,
        suggestion,
        serviceID: user?.service_id,
      });
      setOpenPreviewSuggestionId(suggestion.id);
      return;
    }
    setOpenPreviewSuggestionId((current) =>
      current === suggestion.id ? null : current
    );
  };

  const handleLoadMore = (
    pipeline: SuggestionPipelines,
    totalCount: number
  ) => {
    const nextVisible =
      (visibleCountByPipeline[pipeline] ?? SUGGESTIONS_PAGE_SIZE) +
      SUGGESTIONS_PAGE_SIZE;
    captureKitSuggestionsLoadMore({
      capture: posthogCapture,
      pipeline,
      visibleCount: Math.min(nextVisible, totalCount),
      totalCount,
    });
    setVisibleCountByPipeline((prev) => ({
      ...prev,
      [pipeline]: nextVisible,
    }));
  };

  return (
    <div className='w-full'>
      <button
        type='button'
        onClick={() => {
          const nextExpanded = !isExpanded;
          captureKitCategoryToggled({
            capture: posthogCapture,
            pipeline: pipelineEnum,
            action: nextExpanded
              ? KitCategoryToggleAction.Expand
              : KitCategoryToggleAction.Collapse,
          });
          setExpandedPipeline((prev) =>
            prev === pipelineEnum ? null : pipelineEnum
          );
        }}
        className={cn(
          'flex w-full items-center justify-between gap-1.5 rounded border px-3 py-1.5 text-left transition-colors cursor-pointer',
          categoryStateClass
        )}
      >
        <Flex align='center' gap='sm' className='min-w-0 flex-1'>
          <SuggestionCategoryIcon pipeline={pipelineEnum} />
          <Typography
            variant='body-xs'
            weight='medium'
            className={cn(
              'min-w-0 flex-1 truncate text-neutral-700',
              isPipelineCompleted && 'text-neutral-500'
            )}
          >
            {getCategoryButtonLabel(pipelineEnum)}
          </Typography>
        </Flex>
        <Flex align='center' gap='xs' shrink='0'>
          {isPipelineCompleted ? (
            <Flex align='center' gap='xs' className='shrink-0'>
              <Typography
                variant='body-xxs'
                weight='medium'
                className='text-accent'
              >
                Done
              </Typography>
              <CheckIcon className='h-3 w-3 !stroke-accent' strokeWidth={2} />
            </Flex>
          ) : (
            <span
              className={cn(
                'flex h-4 min-w-[20px] items-center justify-center rounded-full px-2 text-[12px]',
                'text-accent-main font-medium'
              )}
            >
              {pendingCount}
              <ListTodoIcon className='h-3 w-3 !stroke-accent ml-1' />
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className='h-4 w-4 text-neutral-500' />
          ) : (
            <ChevronDown className='h-4 w-4 text-neutral-500' />
          )}
        </Flex>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            key={`category-${pipelineEnum}`}
            {...KIT_ANIMATIONS.toggle}
            className='overflow-hidden'
          >
            <Flex
              direction='col'
              gap='sm'
              data-kit-category-tree=''
              className='mt-1.5 ml-3 border-l border-neutral-300'
            >
              {additionalSuggestions && (
                <Flex direction='row' gap='xs' wrap='wrap' className='w-full'>
                  {additionalSuggestions.rows}
                </Flex>
              )}
              {isQuickQuote && quickQuoteBatches
                ? (() => {
                    const flattened = quickQuoteBatches.flatMap(
                      (batch, batchIndex) =>
                        batch.map((suggestion) => ({
                          suggestion,
                          batchIndex,
                          batch,
                        }))
                    );
                    const totalCount = flattened.length;
                    const visibleLimit = Math.min(
                      visibleCountByPipeline[SuggestionPipelines.QuickQuote] ??
                        SUGGESTIONS_PAGE_SIZE,
                      totalCount
                    );
                    const visibleItems = flattened.slice(0, visibleLimit);
                    const visibleByBatch = groupBy(
                      visibleItems,
                      (x) => x.batchIndex
                    );
                    const batchIndicesInOrder = Object.keys(
                      visibleByBatch
                    ).sort((a, b) => Number(a) - Number(b));
                    let globalIndex = 0;
                    return (
                      <Flex direction='col' gap='sm' className='w-full'>
                        {batchIndicesInOrder.map((batchIndexKey) => {
                          const batchIndex = Number(batchIndexKey);
                          const items = visibleByBatch[batchIndex] ?? [];
                          const batch = quickQuoteBatches[batchIndex];
                          const pendingBatch = batch.filter(
                            (s) =>
                              isQuoteRequestActionable(s) &&
                              !isBlockedSpecialTransportSuggestion(s)
                          );
                          const allPendingAlreadyApplied =
                            pendingBatch.length > 0 &&
                            pendingBatch.every((s) =>
                              batchSelectedSuggestionIds.includes(s.id)
                            );
                          const batchTitleContext = {
                            ...titleContext,
                            showQuickQuoteDestinationOnly:
                              shouldShowBatchLTLDestinationOnly(
                                batch,
                                batchLTLReplySummary
                              ),
                          };
                          const showApplyAllLanes =
                            isBatchQuoteEnabled &&
                            pendingBatch.length > 1 &&
                            !allPendingAlreadyApplied;
                          return (
                            <Flex
                              key={`batch-${batch[0]?.id ?? batchIndex}`}
                              direction='col'
                              gap='xs'
                              className='w-full'
                            >
                              {batch.length > 1 && (
                                <Flex
                                  direction='row'
                                  align='center'
                                  justify='between'
                                  className='w-full'
                                >
                                  <Typography
                                    variant='body-xxs'
                                    weight='medium'
                                    className='ml-4 text-neutral-500'
                                  >
                                    {getQuickQuoteBatchLabel(batch)}
                                  </Typography>
                                  {showApplyAllLanes && (
                                    <button
                                      type='button'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleApplyBatch(pendingBatch);
                                      }}
                                      className='border-none text-accent-600 underline cursor-pointer text-[10px]'
                                    >
                                      Apply all lanes
                                    </button>
                                  )}
                                </Flex>
                              )}
                              <Flex
                                direction='row'
                                gap='xs'
                                wrap='wrap'
                                className='w-full'
                              >
                                {items.map(({ suggestion }) => {
                                  const index = globalIndex++;
                                  const isSelected =
                                    clickedSuggestion?.id === suggestion.id ||
                                    batchSelectedSuggestionIds.includes(
                                      suggestion.id
                                    );
                                  const isDone =
                                    suggestion.status ===
                                    SuggestionStatus.Accepted;
                                  const { ordinal, descriptor, fullTitle } =
                                    getSuggestionTitleParts(
                                      suggestion,
                                      pipelineEnum,
                                      index,
                                      undefined,
                                      batchTitleContext
                                    );
                                  return (
                                    <Suggestion
                                      key={suggestion.id}
                                      suggestion={suggestion}
                                      ordinal={ordinal}
                                      descriptor={descriptor}
                                      fullTitle={fullTitle}
                                      disabled={isDone}
                                      isSelected={isSelected}
                                      isPreviewOpen={
                                        openPreviewSuggestionId ===
                                        suggestion.id
                                      }
                                      onPreviewOpenChange={(open) =>
                                        handlePreviewOpenChange(
                                          suggestion,
                                          open
                                        )
                                      }
                                      onRowPointerEnter={() =>
                                        onSuggestionRowPointerEnter(
                                          suggestion.id
                                        )
                                      }
                                      onApply={(e) => {
                                        e.stopPropagation();
                                        handleApplySuggestion(suggestion);
                                      }}
                                      onDismiss={() =>
                                        handleDismissSuggestion(suggestion)
                                      }
                                    />
                                  );
                                })}
                              </Flex>
                            </Flex>
                          );
                        })}
                        {visibleLimit < totalCount && (
                          <button
                            type='button'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLoadMore(
                                SuggestionPipelines.QuickQuote,
                                totalCount
                              );
                            }}
                            className='mx-auto my-1 text-accent-600 hover:underline cursor-pointer text-[11px]'
                          >
                            {`+ Show ${Math.min(
                              SUGGESTIONS_PAGE_SIZE,
                              totalCount - visibleLimit
                            )} more`}
                          </button>
                        )}
                      </Flex>
                    );
                  })()
                : (() => {
                    const totalCount = validSuggestions.length;
                    const visibleLimit = Math.min(
                      visibleCountByPipeline[pipelineEnum] ??
                        SUGGESTIONS_PAGE_SIZE,
                      totalCount
                    );
                    const visibleSuggestions = validSuggestions.slice(
                      0,
                      visibleLimit
                    );
                    const repeatedTitleIds = getRepeatedLoadBuildingIds(
                      validSuggestions,
                      titleContext
                    );
                    return (
                      <Flex direction='col' gap='xs' className='w-full'>
                        <Flex
                          direction='row'
                          gap='xs'
                          wrap='wrap'
                          className='w-full'
                        >
                          {visibleSuggestions.map((suggestion, index) => {
                            const isSelected =
                              clickedSuggestion?.id === suggestion.id;
                            const isDone =
                              suggestion.status === SuggestionStatus.Accepted;
                            const { ordinal, descriptor, fullTitle } =
                              getSuggestionTitleParts(
                                suggestion,
                                pipelineEnum,
                                index + (additionalSuggestions?.count ?? 0),
                                undefined,
                                {
                                  ...titleContext,
                                  showLoadBuildingDestination:
                                    repeatedTitleIds.has(suggestion.id),
                                }
                              );
                            return (
                              <Suggestion
                                key={suggestion.id}
                                suggestion={suggestion}
                                ordinal={ordinal}
                                descriptor={descriptor}
                                fullTitle={fullTitle}
                                disabled={isDone}
                                isSelected={isSelected}
                                isPreviewOpen={
                                  openPreviewSuggestionId === suggestion.id
                                }
                                onPreviewOpenChange={(open) =>
                                  handlePreviewOpenChange(suggestion, open)
                                }
                                onRowPointerEnter={() =>
                                  onSuggestionRowPointerEnter(suggestion.id)
                                }
                                onApply={(e) => {
                                  e.stopPropagation();
                                  handleApplySuggestion(suggestion);
                                }}
                                onDismiss={() =>
                                  handleDismissSuggestion(suggestion)
                                }
                              />
                            );
                          })}
                        </Flex>
                        {visibleLimit < totalCount && (
                          <button
                            type='button'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLoadMore(pipelineEnum, totalCount);
                            }}
                            className='ml-4 self-start rounded border border-neutral-300 bg-neutral-50 px-2 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-100 hover:border-neutral-400 transition-colors'
                          >
                            {`+ Load more (${Math.min(
                              SUGGESTIONS_PAGE_SIZE,
                              totalCount - visibleLimit
                            )} more)`}
                          </button>
                        )}
                      </Flex>
                    );
                  })()}
            </Flex>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function filterValidDisplaySuggestions(
  suggestions: GenericSuggestion[]
): GenericSuggestion[] {
  return suggestions.filter((suggestion) => {
    const changes =
      suggestion.pipeline === SuggestionPipelines.CheckCall
        ? suggestion.suggested.checkCallChanges
        : suggestion.suggested;
    const flattenedChanges = flattenSuggestionChanges(changes);
    return hasPreviewableSuggestionChanges(
      flattenedChanges,
      suggestion.pipeline
    );
  });
}
